import type { FilterType } from "@/components/@types";
import { SharedReportFilterBar } from "@/app/share/report/SharedReportFilterBar";
import type { SharedViewLayout } from "@/app/share/report/sharedReportTypes";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function SharedReportTitleRow({
  title,
  groups,
  selectedGroupId,
  onGroupChange,
  showGroupSelect,
  compact = false,
}: {
  title: string;
  groups: Array<{ id: string; label: string }>;
  selectedGroupId: string;
  onGroupChange: (groupId: string) => void;
  showGroupSelect: boolean;
  compact?: boolean;
}) {
  if (!title && !showGroupSelect) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center",
        compact ? "gap-2" : "gap-4",
        compact && "flex-1",
        "min-w-0",
        compact ? "" : "min-h-12",
      )}
    >
      {title && (
        <Typography
          level="h3"
          className={cn(
            "truncate",
            compact && "flex-1",
            "min-w-0",
            compact ? "shrink" : "shrink-0",
          )}
        >
          {title}
        </Typography>
      )}
      {showGroupSelect && (
        <Select
          value={selectedGroupId}
          onValueChange={(value) => {
            if (value) onGroupChange(value);
          }}
        >
          <SelectTrigger
            size="sm"
            className={cn(
              title && !compact ? "ml-auto" : "ml-0",
              compact ? "min-w-28" : "min-w-40",
              compact ? "max-w-32" : "max-w-full",
              "shrink-0",
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export function SharedReportTopbar({
  title,
  layout,
  groups,
  selectedGroupId,
  onGroupChange,
  filters,
  onFiltersChange,
  showTitle = true,
  showGroupSelect,
  showFilters,
}: {
  title: string;
  layout: SharedViewLayout;
  groups: Array<{ id: string; label: string }>;
  selectedGroupId: string;
  onGroupChange: (groupId: string) => void;
  filters: FilterType[];
  onFiltersChange: (filters: FilterType[]) => void;
  showTitle?: boolean;
  showGroupSelect: boolean;
  showFilters: boolean;
}) {
  if ((!showTitle || !title) && !showGroupSelect && !showFilters) {
    return null;
  }

  const hasHeaderRow = (showTitle && Boolean(title)) || showGroupSelect;

  return (
    <div className="flex flex-col min-w-0 h-full bg-background">
      {hasHeaderRow && (
        <SharedReportTitleRow
          title={showTitle ? title : ""}
          groups={groups}
          selectedGroupId={selectedGroupId}
          onGroupChange={onGroupChange}
          showGroupSelect={showGroupSelect}
        />
      )}

      {showFilters && (
        <div className="flex items-center justify-start min-h-12 overflow-y-hidden">
          <SharedReportFilterBar
            layout={layout}
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        </div>
      )}
    </div>
  );
}
